import React, { useState, useEffect, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import './../styles/styles.css';
import CheckoutForm from '../components/CheckoutForm';

import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_API_URL;

export default function Checkout() {
    const { user, isAuthenticated, loading } = useAuth();
    const { cartItems, clearCart } = useCart();
    const navigate = useNavigate();

    // NOUVEAUX ÉTATS : Pour stocker les détails client et gérer le chargement/erreur de l'API
    const [customerDetails, setCustomerDetails] = useState({
        customerId: null,
        firstName: '',
        lastName: ''
    });
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);

    // NOUVEL ÉTAT : Pour le code promo saisi par l'utilisateur
    const [promoCode, setPromoCode] = useState('');

    // ----------------------------------------------------------------------
    // NOUVEAUX ÉTATS POUR LA LOGIQUE DU CODE PROMO
    // ----------------------------------------------------------------------
    const [discountAmount, setDiscountAmount] = useState(0); // Montant de la réduction appliquée
    const [promoMessage, setPromoMessage] = useState(null);  // Message (erreur/succès)
    const [isApplyingPromo, setIsApplyingPromo] = useState(false); // État de chargement du bouton
    // ----------------------------------------------------------------------

    // Récupération de la clé publique Stripe (Logique inchangée)
    const stripePromise = useMemo(() => {
        // ... (Logique inchangée pour la clé Stripe)
        async function fetchStripeKey() {
            try {
                const res = await fetch(`${API_URL}/payment/get-stripe-public-key.php`);
                const data = await res.json();
                if (data.stripePublicKey) {
                    return loadStripe(data.stripePublicKey);
                } else {
                    console.error('Erreur: Clé publique Stripe non trouvée dans la réponse.');
                    return null;
                }
            } catch (err) {
                console.error('Erreur lors de la récupération de la clé Stripe :', err);
                return null;
            }
        }
        return fetchStripeKey();
    }, [API_URL]);

    // Redirection si non authentifié (Logique inchangée)
    useEffect(() => {
        if (!loading && !isAuthenticated) {
            navigate('/login');
        }
    }, [loading, isAuthenticated, navigate]);

    // Appel API pour récupérer les détails client (Logique inchangée)
    useEffect(() => {
        if (isAuthenticated && user?.userId && !customerDetails.customerId && !detailsLoading) {
            setDetailsLoading(true);
            setDetailsError(null);

            async function fetchCustomerDetails() {
                try {
                    const token = localStorage.getItem('token'); 
                    
                    const res = await fetch(`${API_URL}/users/get-user.php?id=${user.userId}`, { 
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }); 
                    
                    if (!res.ok) {
                        throw new Error(`Erreur HTTP lors de la récupération des détails : ${res.status}`);
                    }
                    
                    const data = await res.json();
                    
                    const userData = data.user; 
                    
                    if (userData && userData.stripe_customer_id) { 
                        setCustomerDetails({
                            customerId: userData.stripe_customer_id, 
                            firstName: userData.first_name,
                            lastName: userData.last_name,
                        });
                    } else {
                        setDetailsError("Détails client reçus sont incomplets. Le customer ID Stripe est manquant.");
                    }
                } catch (err) {
                    console.error('Erreur lors de la récupération des détails client :', err);
                    setDetailsError('Impossible de récupérer les détails client. ' + err.message);
                } finally {
                    setDetailsLoading(false);
                }
            }
            fetchCustomerDetails();
        }
    }, [isAuthenticated, user?.userId, customerDetails.customerId, detailsLoading, API_URL]);

    // ----------------------------------------------------------------------
    // NOUVELLE FONCTION : Appliquer le code promo via une API
    // ----------------------------------------------------------------------
    const applyPromoCode = async () => {
        if (!promoCode) {
            setPromoMessage({ text: "Veuillez entrer un code promo.", type: 'error' });
            setDiscountAmount(0);
            return;
        }

        setIsApplyingPromo(true);
        setPromoMessage(null);
        setDiscountAmount(0); // Réinitialiser le montant de la réduction

        try {
            // NOTE : Vous devrez créer cet endpoint (ex: validate-promo.php) sur votre backend.
            const response = await fetch(`${API_URL}/payment/validate-promo.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    promoCode: promoCode,
                    cartItems: cartItems.map(item => ({ id: item.id, price: item.price, stripe_price_id: item.stripe_price_id, quantity: item.quantity })),
                }),
            });

            const data = await response.json();

            if (data.error) {
                // Le code est invalide ou expiré
                setPromoMessage({ text: data.error, type: 'error' });
                setDiscountAmount(0);
            } else if (data.discountAmount !== undefined) {
                // Le code est valide
                const discount = parseFloat(data.discountAmount);
                setDiscountAmount(discount);
                setPromoMessage({ text: `Code appliqué ! Vous économisez ${discount.toFixed(2)} €`, type: 'success' });
            } else {
                setPromoMessage({ text: "Erreur de validation du code. Réponse inattendue du serveur.", type: 'error' });
                setDiscountAmount(0);
            }

        } catch (error) {
            console.error('Erreur lors de l\'appel API du code promo :', error);
            setPromoMessage({ text: "Erreur de connexion lors de la validation du code.", type: 'error' });
            setDiscountAmount(0);
        } finally {
            setIsApplyingPromo(false);
        }
    };
    // ----------------------------------------------------------------------


    // GESTION DES CHARGEMENTS (Logique inchangée)
    if (loading || !user || detailsLoading) {
        return (
            <section className="section-black" style={{ textAlign: 'center' }}>
                <h2>Chargement...</h2>
            </section>
        );
    }
    
    // Afficher une erreur si la récupération des détails a échoué (Logique inchangée)
    if (detailsError) {
        return (
            <section className="section-cream" style={{ textAlign: 'center' }}>
                <h2>Erreur de données client.</h2>
                <p>{detailsError}</p>
                <div className="cta-button" onClick={() => window.location.reload()}>Recharger la page</div>
            </section>
        );
    }

    // GESTION du panier vide (Logique inchangée)
    if (cartItems.length === 0) {
        return (
            <section className="section-cream" style={{ textAlign: 'center' }}>
                <h2>Votre panier est vide.</h2>
                <p>Veuillez ajouter des produits avant de passer à la caisse.</p>
                <div className="cta-button" onClick={() => navigate('/store')}>Aller à la boutique</div>
            </section>
        );
    }

    // CALCUL DU TOTAL: Le montant initial
    const initialTotal = cartItems.reduce((total, item) => total + parseFloat(item.price) * item.quantity, 0); 
    
    // CALCUL DU TOTAL FINAL: Montant initial moins la réduction
    const finalTotal = (initialTotal - discountAmount) > 0 ? (initialTotal - discountAmount) : 0;


    return (
        <div>
            <section className="section-black checkout-header">
                <h1>Finaliser votre commande</h1>
                <p>Entrez vos informations pour procéder au paiement sécurisé.</p>
            </section>

            <section className="section-cream checkout-content">
                <div className="checkout-container">
                    <div className="cart-summary">
                        <h3>Résumé de la commande</h3>
                        {cartItems.map(item => (
                            <div key={item.id} className="summary-row">
                                <span>{item.title}</span>
                                <span>{parseFloat(item.price).toFixed(2)} €</span>
                            </div>
                        ))}
                        
                        {/* ---------------------------------------------------------------------- */}
                        {/* BLOC CODE PROMO CORRIGÉ */}
                        {/* ---------------------------------------------------------------------- */}
                        <div style={{ margin: '15px 0' }}>
                            {/* Le label est maintenant au-dessus du conteneur flex */}
                            <label htmlFor="promo-code" style={{ display: 'block', marginBottom: '5px' }}>Code Promo :</label>

                            <div className="promo-code-input-group" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <input
                                    id="promo-code"
                                    type="text"
                                    value={promoCode}
                                    onChange={(e) => {
                                        setPromoCode(e.target.value);
                                        if (promoMessage) setPromoMessage(null); // Effacer le message lors de la frappe
                                        if (discountAmount > 0) setDiscountAmount(0); // Réinitialiser le discount
                                    }}
                                    placeholder="Entrez votre code"
                                    disabled={isApplyingPromo}
                                    // flexGrow: 1 permet à l'input de prendre l'espace restant
                                    style={{ flexGrow: 2, padding: '8px', minWidth: '5 rem', marginTop: '1rem' }} 
                                />
                                <button
                                    type="button"
                                    onClick={applyPromoCode}
                                    disabled={isApplyingPromo || !promoCode}
                                    className="cta-button"
                                    // Utilisation de `height: fit-content` et padding pour s'assurer qu'il s'aligne
                                    style={{ padding: '8px 15px', whiteSpace: 'nowrap', height: 'fit-content', maxWidth : '5 rem' }}
                                >
                                    {isApplyingPromo ? 'Vérif...' : 'Appliquer'}
                                </button>
                            </div>
                        </div>
                        
                        {promoMessage && (
                            <div 
                                className={`promo-message-box ${promoMessage.type}`}
                                style={{ color: promoMessage.type === 'error' ? '#FF4444' : '#00AA00', marginBottom: '10px', fontSize: '0.9em' }}
                            >
                                {promoMessage.text}
                            </div>
                        )}

                        {discountAmount > 0 && (
                            <div className="summary-row discount-row" style={{ color: '#00AA00', borderTop: '1px solid #CCC', paddingTop: '5px' }}>
                                <span>Réduction Code Promo :</span>
                                <span>-{discountAmount.toFixed(2)} €</span>
                            </div>
                        )}
                        {/* ---------------------------------------------------------------------- */}
                        
                        <div className="summary-row total">
                            <strong>Total :</strong>
                            <strong>{finalTotal.toFixed(2)} €</strong>
                        </div>
                        
                        <Elements stripe={stripePromise}>
                            <CheckoutForm 
                                email={user.email} 
                                firstName={customerDetails.firstName}
                                lastName={customerDetails.lastName}
                                userId={user.userId} 
                                customerId={customerDetails.customerId}
                                context="checkout"
                                cartItems={cartItems}
                                onPaymentSuccess={clearCart}
                                // PROPS MISES À JOUR
                                promoCode={promoCode} 
                                discountAmount={discountAmount} 
                            />
                        </Elements>

                    </div>

                </div>
            </section>
        </div>
    );
}